import { format } from 'date-fns';
import { Language } from '@app/constants/enums';
import { dateFnsLocaleMap } from '@app/constants/app';

/**
 * The calendar day a date falls on, as midnight UTC.
 *
 * Midnight UTC rather than local, because `fullDate` below — which every day comparison in the app goes
 * through — reads a date's UTC part as its day. Local midnight on the wire is the evening before in UTC
 * for anyone east of Greenwich, so a day filed from Brussels would be read as the day before it and the
 * day it was filed for would never leave "To report".
 */
export const calendarDay = (date: Date | string): Date => {
  const local = new Date(date);

  return new Date(Date.UTC(local.getFullYear(), local.getMonth(), local.getDate()));
};

/**
 * The local-time date that renders as the day a midnight-UTC day names — the inverse of `calendarDay`.
 * Rebuilt from the UTC parts, not shifted by the offset: on a DST day that offset lands a day early.
 */
export const localDay = (date: Date | string | number): Date => {
  const parsed = new Date(date);

  return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
};

export const fullDate = date => format(localDay(date), 'yyyy-MM-dd');
export const shortDate = date => format(localDay(date), 'eee, dd MMMM');
export const monthAndDate = date => format(localDay(date), 'dd MMMM');
export const year = date => format(localDay(date), 'yyyy');

export const addDays = (date: Date, days: number): Date => {
  if (days === 0) {
    return date;
  }
  const copy = new Date(Number(date));
  copy.setDate(date.getDate() + days);
  return copy;
};

/**
 * The `yyyy-mm-dd` key for the calendar day an instant falls on — today, for `new Date()`.
 *
 * **`fullDate(new Date())` is not this**, and every "is this today?" in the app has to come through here
 * instead. The formatters above read the UTC parts of a day that is already anchored to midnight UTC;
 * an instant is not anchored to anything, so reading its UTC parts rolls it past midnight —
 * the last hours of the evening west of Greenwich read as tomorrow, and the first hours after midnight
 * east of it as yesterday. An instant becomes a day by taking its local parts, which is `calendarDay`.
 */
export const dayKey = (instant: Date): string => fullDate(calendarDay(instant));

export const renderDate = (date: Date | string) => {
  const day = fullDate(date);

  if (day === dayKey(new Date())) return `Today, ${monthAndDate(date)}`;
  // Stepped by local date parts rather than by subtracting 24 hours, which is not a day on the two
  // mornings a year the clocks move.
  if (day === dayKey(addDays(new Date(), -1))) return `Yesterday, ${monthAndDate(date)}`;
  return shortDate(date);
};

/** 'yyyy-MM-dd' strings parse as UTC midnight, so getUTCDay reliably picks out Sat/Sun. */
export const isWeekendDate = (date: string): boolean => [0, 6].includes(new Date(date).getUTCDay());

export const toFixedHours = (hours: number): number => (hours % 1 === 0 ? hours : Number(hours.toFixed(2)));
export const hoursToMinutes = (minutes?: number): number => (minutes || 0) * 60 - (((minutes || 0) * 60) % 1) || 0;

/** A calendar day, in the reader's language. For an instant — when someone approved — `formatTimestamp`. */
export const formatDateString = (
  date: string | number | Date | undefined | null,
  language: Language,
  dateFormat?: string,
): string => {
  if (!date) return '';

  return format(localDay(date), dateFormat || 'dd-MMM-yyyy', {
    locale: dateFnsLocaleMap[language],
  });
};

/** A moment in time, in the reader's language and zone: read straight, not as a midnight-UTC day. */
export const formatTimestamp = (
  date: string | number | Date | undefined | null,
  language: Language,
  dateFormat?: string,
): string => {
  if (!date) return '';

  return format(date instanceof Date ? date : new Date(date), dateFormat || 'dd-MMM-yyyy', {
    locale: dateFnsLocaleMap[language],
  });
};

export const isDST = (d: Date) => {
  const jan = new Date(d.getFullYear(), 0, 1).getTimezoneOffset();
  const jul = new Date(d.getFullYear(), 6, 1).getTimezoneOffset();
  return Math.max(jan, jul) !== d.getTimezoneOffset();
};
